import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../hooks/useSEO';

export default function KeelungPage() {
  useSEO({
    title: '基隆搬家公司推薦｜專業搬家服務｜基隆市搬家費用估價 - 安心搬家',
    description: '基隆搬家公司推薦首選！提供基隆市7區專業搬家服務，包含仁愛區、信義區、中正區、中山區、安樂區、暖暖區、七堵區。24小時服務，免費估價，專業團隊，搬家零煩惱。立即預約基隆搬家服務！',
    keywords: '基隆搬家,基隆搬家公司,仁愛區搬家,信義區搬家,中正區搬家,基隆市搬家費用,基隆搬家推薦,基隆搬家估價,安樂區搬家,七堵區搬家',
    ogImage: 'https://public.readdy.ai/ai/img_res/dd82de3b3477895703223ae65fe0d9f8.jpg'
  });

  useEffect(() => {
    // 加入 Schema.org LocalBusiness 結構化資料
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "安心搬家 - 基隆搬家服務",
      "image": "https://readdy.ai/api/search-image?query=professional%20moving%20service%20team%20transporting%20furniture%20in%20Keelung%20harbor%20city%20residential%20building%20with%20ocean%20view%20and%20organized%20packing%20materials%20showing%20careful%20handling%20process&width=1200&height=630&seq=keelung-moving-og-001&orientation=landscape",
      "@id": "https://moving-company.com/area/keelung",
      "url": "https://moving-company.com/area/keelung",
      "telephone": "+886-2-1234-5678",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "仁愛區忠一路",
        "addressLocality": "基隆市",
        "postalCode": "200",
        "addressCountry": "TW"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 25.1276,
        "longitude": 121.7392
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "sameAs": [
        "https://www.facebook.com/movingcompany",
        "https://www.instagram.com/movingcompany"
      ],
      "priceRange": "$$",
      "areaServed": [
        { "@type": "City", "name": "仁愛區" },
        { "@type": "City", "name": "信義區" },
        { "@type": "City", "name": "中正區" },
        { "@type": "City", "name": "中山區" },
        { "@type": "City", "name": "安樂區" },
        { "@type": "City", "name": "暖暖區" },
        { "@type": "City", "name": "七堵區" }
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "142"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const keelungDistricts = [
    { name: '中正區', features: ['基隆港', '和平島', '正濱漁港'], highlight: true },
    { name: '仁愛區', features: ['基隆車站', '廟口夜市', '海洋廣場'], highlight: true },
    { name: '信義區', features: ['深澳漁港', '八斗子', '潮境公園'], highlight: true },
    { name: '中山區', features: ['外木山', '協和發電廠', '中山高架橋'] },
    { name: '安樂區', features: ['情人湖', '大武崙砲台', '外寮漁港'] },
    { name: '暖暖區', features: ['暖暖車站', '十分瀑布', '暖江橋'] },
    { name: '七堵區', features: ['七堵車站', '友蚋溪', '基隆河'] }
  ];

  const services = [
    {
      icon: 'ri-ship-2-line',
      title: '港口物流搬遷',
      description: '基隆港區貨物、船舶用品專業搬運',
      color: 'teal'
    },
    {
      icon: 'ri-mountain-line',
      title: '山坡地形搬家',
      description: '山城陡坡、狹窄巷弄專業搬遷服務',
      color: 'emerald'
    },
    {
      icon: 'ri-building-3-line',
      title: '老舊建築搬遷',
      description: '港都老屋、無電梯公寓搬家經驗',
      color: 'cyan'
    },
    {
      icon: 'ri-foggy-line',
      title: '海港氣候適應',
      description: '配合海港氣候，雨天搬家防護完善',
      color: 'blue'
    }
  ];

  const faqs = [
    {
      question: '基隆搬家公司推薦哪一家？',
      answer: '建鈞精緻搬家服務基隆市 7 個行政區，4.8 分高評價，156 則客戶好評，熟悉基隆港口、山城地形特色，提供 24 小時免費估價服務。'
    },
    {
      question: '基隆搬家費用怎麼算？',
      answer: '依據搬家規模、樓層、距離計價。套房搬遷 3,000 元起，家庭搬家 3,000-8,000 元，辦公室搬遷 5,000-20,000 元。山坡地形、無電梯會酌收樓層費，雙邊電梯優惠價 3,000 元起。'
    },
    {
      question: '基隆搬家當天可以預約嗎？',
      answer: '建議提前 3-7 天預約，旺季（週末、月底）建議提前 2 週。急件可來電 0978-900-081 詢問，我們會盡力安排。基隆多雨，建議預約時考慮天氣因素。'
    },
    {
      question: '基隆山城搬家要注意什麼？',
      answer: '基隆山城地形特殊，狹窄巷弄、陡峭坡道較多。我們使用小型搬運車輛確保通行，配備專業繩索固定技術，並熟悉各區域道路狀況，確保搬運安全。'
    },
    {
      question: '基隆雨天搬家怎麼辦？',
      answer: '基隆多雨氣候，我們提供專業防水包裝，雨天搬家照常進行。所有物品都會使用防水材料保護，車輛配備防水設備，確保您的物品不受潮濕影響。'
    },
    {
      question: '基隆港區搬家有什麼特別服務？',
      answer: '我們熟悉基隆港作業時程，可配合船期進行港區物流搬運，並了解潮汐變化，選擇最佳搬運時機。提供船舶用品、港口貨物專業搬運服務。'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-50 via-white to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              基隆搬家公司推薦
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              提供基隆市7區專業搬家服務，24小時全年無休，免費到府估價，專業團隊讓您搬家零煩惱
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/quote"
                className="inline-flex items-center px-8 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                立即預約估價
              </Link>
              <Link
                to="/calculator"
                className="inline-flex items-center px-8 py-3 bg-white text-orange-500 font-semibold rounded-lg border-2 border-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calculator-line mr-2"></i>
                費用試算
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">基隆市 7 區完整服務</h2>
            <p className="text-lg text-gray-600">熟悉基隆各區域地形特色，提供在地化專業搬家服務</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {keelungDistricts.map((district, index) => (
              <div 
                key={index} 
                className={`rounded-xl p-6 transition-all hover:shadow-lg hover:-translate-y-1 ${
                  district.highlight 
                    ? 'bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-200' 
                    : 'bg-white shadow-md'
                }`}
              >
                <h3 className={`font-bold text-lg mb-3 flex items-center ${
                  district.highlight ? 'text-teal-700' : 'text-gray-900'
                }`}>
                  <i className="ri-map-2-line mr-2"></i>
                  {district.name}
                  {district.highlight && (
                    <span className="ml-2 bg-teal-500 text-white text-xs px-2 py-1 rounded-full">熱門</span>
                  )}
                </h3>
                <div className="space-y-1">
                  {district.features.map((feature, idx) => (
                    <div key={idx} className="text-sm text-gray-600 flex items-center">
                      <i className="ri-checkbox-circle-line text-teal-500 mr-2 text-xs"></i>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">基隆港都特色服務</h2>
            <p className="text-lg text-gray-600">針對基隆港都山城地形特色，提供專業搬遷解決方案</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="text-center group">
                <div className={`w-20 h-20 bg-${service.color}-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-${service.color}-200 transition-all group-hover:scale-110 shadow-md`}>
                  <i className={`${service.icon} text-3xl text-${service.color}-600`}></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">基隆搬家常見問題</h2>
              <p className="text-lg text-gray-600">為您解答基隆搬家的各種疑問</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-question-line text-teal-600"></i>
                    </div>
                    <h3 className="font-bold text-lg text-gray-900">{faq.question}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed pl-11">{faq.answer}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold transition-colors"
              >
                查看更多常見問題
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Geographic Challenges */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">山城港都搬遷挑戰</h2>
              <p className="text-lg text-gray-600">克服基隆特殊地理環境，提供最專業的搬家解決方案</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-road-map-line text-xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-3">狹窄巷弄通行</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  熟悉基隆山城狹窄巷弄，使用小型搬運車輛確保通行無阻
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-direction-line text-xl text-blue-600"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-3">陡坡搬運技術</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  山城陡峭坡道搬運經驗豐富，專業繩索固定技術確保安全
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-line text-xl text-indigo-600"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-3">老屋結構熟悉</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  港都老舊建築結構熟悉，無電梯公寓搬運經驗豐富
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-cloudy-2-line text-xl text-green-600"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-3">雨天搬家防護</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  配合基隆多雨氣候，專業防水包裝，雨天搬家照常進行
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-ship-line text-xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-3">港口作業配合</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  熟悉基隆港作業時程，配合船期進行港區物流搬運
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-time-line text-xl text-purple-600"></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-3">潮汐時間掌握</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  配合港區潮汐變化，選擇最佳搬運時機確保作業順利
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            準備在基隆搬家嗎？
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            立即聯繫我們，獲得專業的基隆港都山城搬家服務評估
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:0978-900-081"
              className="bg-white text-teal-600 hover:bg-teal-50 px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line"></i>
              電話諮詢：0978-900-081
            </a>
            <a
              href="https://line.me/ti/p/@285yavdh"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-line-fill"></i>
              官方LINE
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
